/*
 * Copyright (c) Hilo 2020.
 * Author: fharold
 *
 * hilo-oauth-manager
 * ClientService.ts
 */

import APIService from "../tools/service/APIService";
import Client from "../models/Client";
import QueryFilter from "../tools/model/pagination/Filters";
import { Listener } from "../tools/service/Service";
import { Selectable } from "../tools/model/form/Selectable";

export interface PaginationFilters {
    page: number;
    size: number;
    sort?: string;
    searchQuery?: string;
}


export default class ClientService extends APIService<Client, Listener<Client>> {
    public static SINGLE_ROUTE = '/oauth/client/{clientId}';
    public static ACTIVATION_ROUTE = '/oauth/client/{clientId}/activate';
    public static LIST_ROUTE = '/oauth/clients';
    public static CLIENT_CHANGE_ROUTE = '/oauth/client/{clientId}/clients';
    public static id = "ClientService";

    public static convertToSelectable(clients: Array<Client>): Array<Selectable<Client>> {
        let result: Array<Selectable<Client>> = [];

        for (let client of clients) {
            result.push({
                value: client,
                name: client.name,
                disabled: false
            })
        }

        return result;
    }

    /**
     * start
     */
    public start() {
        super.start();
    }

    /**
     * stop
     */
    public stop() {
        super.stop();
    }

    /**
     * createClient
     * @return {Promise<Client>}
     * @param dto
     */
    public async createClient(dto: FormData): Promise<Client> {
        return await this.post(ClientService.LIST_ROUTE, dto);
    }

    /**
     * updateClient
     * @return {Promise<Client>}
     * @param dto
     * @param client
     */
    public async updateClient(client: Client, dto: FormData): Promise<Client> {
        const route = this.httpService.buildRoute(ClientService.SINGLE_ROUTE, {
            clientId: client.id
        });

        return await this.put(route, dto);
    }

    /**
     * findById
     * @returns {Promise<void>}
     */
    public async findById(id: string): Promise<Client> {
        const route = this.httpService.buildRoute(ClientService.SINGLE_ROUTE, {
            clientId: id
        });

        return await this.getOne<Client>(route);
    }

    /**
     * findAll
     * @returns {Promise<Client[]>}
     */
    public async findAll(): Promise<Client[]> {
        const clientFilters: QueryFilter<PaginationFilters> = new QueryFilter<PaginationFilters>({
            page: 0,
            size: 10000
        });

        return await this.getList<Client, PaginationFilters>(ClientService.LIST_ROUTE, clientFilters);
    }

    /**
     * updateClients
     * @param client
     * @param dto
     * @returns {Promise<void>}
     */
    public async updateClients(client: Client, dto: FormData) {
        const route = this.httpService.buildRoute(ClientService.CLIENT_CHANGE_ROUTE, {
            clientId: client.id
        });
        return await this.post(route, dto);
    }

    /**
     * updateClients
     * @param client
     * @param dto
     * @returns {Promise<void>}
     */
    public async setActivation(client: Client, dto: FormData) {
        const route = this.httpService.buildRoute(ClientService.ACTIVATION_ROUTE, {
            clientId: client.id
        });
        return await this.post(route, dto);
    }
}
